import { Link, useParams } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { Card } from "../../../components/Card";
import { Chip } from "../../../components/Chip";
import { Notice } from "../../../components/Notice";
import { StatusBadge } from "../../../components/StatusBadge";
import { db } from "../../../lib/db";
import type { VerificationState } from "../../../types";

const verificationLabel: Record<VerificationState, string> = {
  SELF_ATTESTED: "Not verified by ClearScope",
  LICENSE_CLAIMED: "License claimed",
  REFERENCE_READY: "References available",
  UNVERIFIED: "Not verified",
};

export function ContractorProfilePage() {
  const { id } = useParams();
  const contractor = useLiveQuery(() => (id ? db.contractors.get(id) : undefined), [id]);

  if (!contractor) {
    return (
      <Notice title="Contractor not found" tone="warning">
        This contractor profile is not present in local storage.
      </Notice>
    );
  }

  return (
    <div className="space-y-6">
      <Link className="text-sm font-semibold text-text-muted hover:text-text" to="/jobs">
        Back to board
      </Link>
      <Card padding="lg" variant="surface">
        <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-20 w-16 shrink-0 items-center justify-center rounded-[var(--radius-surface)] border border-border bg-surface-sunken font-mono text-lg font-semibold text-text shadow-[var(--shadow-1)]">
              {contractor.displayName.slice(0, 2)}
            </div>
            <div>
              <p className="page-kicker">{contractor.serviceArea}</p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <h1 className="page-title font-semibold">{contractor.businessName}</h1>
                <StatusBadge
                  label={verificationLabel[contractor.verificationState]}
                  level="AMBER"
                  tone="soft"
                />
              </div>
              <p className="mt-2 text-text-muted">{contractor.displayName}</p>
              <Notice compact title="Verify before hiring" tone="warning">
                ClearScope has not checked identity, license, insurance, references, or fit.
              </Notice>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid gap-5 lg:grid-cols-[1fr_360px] lg:items-start">
        <section className="space-y-5">
          <Card padding="lg" variant="surface">
            <h2 className="text-xl font-semibold text-text">Services</h2>
            <p className="mt-2 text-sm font-semibold text-text-subtle">Open to</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {contractor.categories.map((category) => (
                <Chip key={category}>{category.replace(/_/g, " ")}</Chip>
              ))}
            </div>
          </Card>

          <Card padding="lg" variant="surface">
            <h2 className="text-xl font-semibold text-text">About this pro</h2>
            <p className="mt-3 text-sm leading-6 text-text-muted">{contractor.trustStatement}</p>
          </Card>

          <Notice title="Profile note" tone="warning">
            {contractor.ratingSignal}
          </Notice>
        </section>

        <aside className="space-y-5">
          <Card padding="lg" variant="surface">
            <h2 className="text-xl font-semibold text-text">Availability</h2>
            <p className="mt-3 text-sm leading-6 text-text-muted">
              {contractor.availabilitySummary}
            </p>
            <div className="mt-5 grid grid-cols-4 gap-2">
              {["W1", "W2", "W3", "W4"].map((week, index) => (
                <Chip
                  key={week}
                  className="justify-center"
                  tone={index < 3 ? "success" : "neutral"}
                >
                  {week}
                </Chip>
              ))}
            </div>
            <dl className="mt-5 space-y-3 text-sm">
              <div>
                <dt className="font-semibold text-text-subtle">Minimum listed job value</dt>
                <dd className="text-text">${contractor.minimumJobValue}</dd>
              </div>
              <div>
                <dt className="font-semibold text-text-subtle">Preferred contact</dt>
                <dd className="text-text">{contractor.contact.primary}</dd>
              </div>
            </dl>
          </Card>
          <Card padding="lg" variant="surface">
            <h2 className="text-xl font-semibold text-text">Before you hire</h2>
            <ul className="mt-3 space-y-2 text-sm leading-6 text-text-muted">
              <li>Ask for current license or insurance where required.</li>
              <li>Confirm final job details, price, schedule, and materials in writing.</li>
              <li>Do not rely on ClearScope as a screening service.</li>
            </ul>
          </Card>
        </aside>
      </div>
    </div>
  );
}
